const DEFAULT_WISHLIST_STATE = {
  active: false,
  inactive: false,
  animate: false,
  deactivate: false,
};

const ACTIVE_WISHLIST_STATE = {
  active: true,
  inactive: false,
  animate: false,
  deactivate: false,
};

/**
 * Build className string for wishlist heart UI.
 * @param {{ active?: boolean, inactive?: boolean, animate?: boolean, deactivate?: boolean }} [state]
 * @returns {string}
 */
export function getWishlistClassName(state = {}) {
  return [
    "wishlist-btn",
    state.active && "active",
    state.inactive && "inactive",
    state.animate && "animate",
    state.deactivate && "deactivate",
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Pure toggle matching the theme's wishlist button class behavior.
 * Firework animation (`animate`) only when becoming active (heart add).
 * @param {{ active?: boolean, inactive?: boolean, animate?: boolean, deactivate?: boolean }} [current]
 * @returns {{ active: boolean, inactive: boolean, animate: boolean, deactivate: boolean }}
 */
export function getNextWishlistState(current = DEFAULT_WISHLIST_STATE) {
  const wasActive = Boolean(current.active);

  if (wasActive) {
    return {
      active: false,
      inactive: true,
      animate: false,
      deactivate: true,
    };
  }

  return {
    active: true,
    inactive: false,
    animate: true,
    deactivate: false,
  };
}

/**
 * Toggle one product's wishlist UI state inside a map keyed by product id.
 * @param {Record<string, object>} wishlistMap
 * @param {string} productId
 * @returns {Record<string, object>}
 */
export function toggleWishlistInMap(wishlistMap = {}, productId) {
  return {
    ...wishlistMap,
    [productId]: getNextWishlistState(wishlistMap[productId]),
  };
}

/**
 * Build UI state map from saved wishlist product ids (or product objects with id).
 * Hydrated items are active without animate so fireworks do not replay on navigation.
 * @param {Array<string|{ id: string }>} wishlistItems
 * @returns {Record<string, object>}
 */
export function buildWishlistUiStateMap(wishlistItems = []) {
  return wishlistItems.reduce((map, item) => {
    const id = typeof item === "string" ? item : item?.id;
    if (id) {
      map[id] = { ...ACTIVE_WISHLIST_STATE };
    }
    return map;
  }, {});
}

/**
 * Clear the temporary animate flag after the firework finishes.
 * @param {Record<string, object>} wishlistMap
 * @param {string} productId
 * @returns {Record<string, object>}
 */
export function clearWishlistAnimateInMap(wishlistMap = {}, productId) {
  const current = wishlistMap[productId];
  if (!current?.animate) {
    return wishlistMap;
  }
  return {
    ...wishlistMap,
    [productId]: {
      ...current,
      animate: false,
    },
  };
}
